/**
 * Unified Collection Routing — FEAT-157 (BET-105 Slice 0)
 *
 * Thin client: delegates to Convex chain.resolveCollection (L5 orchestration).
 * All entry-creation surfaces call this; Convex runs LLM → heuristic → typeHint.
 *
 * Chain: DEC-163 (L5 placement), FEAT-157, FEAT-159 (confidence gating)
 */

import type { GatewayRouteReturnByName } from "@productbrain/kernel-client";
import { kernelCall, getAgentSessionId } from "../client.js";

// ── Types ──────────────────────────────────────────────────────────────────
// PR #395 (Lane C S3b annotation pass): `ResolvedCollection` used to be a
// hand-copied mirror of `convex/agentKnowledge/resolveCollection.ts`'s own
// exported interface. Annotating this call site with the generic
// `GatewayRouteReturnByName<'chain.resolveCollection'>` alias surfaced a real
// drift: the hand copy was missing the `candidates` field entirely and had a
// narrower `thinkingLayer?: string` (server reality: `string | null`). Now
// DERIVED from the server type directly — a future server-side shape change
// is a compile error here, not a silent, unnoticed divergence.

export type ResolvedCollection = NonNullable<GatewayRouteReturnByName<"chain.resolveCollection">>;

// ── Main entry point (thin client) ───────────────────────────────────────

export async function resolveCollection(params: {
  name: string;
  description: string;
  typeHint?: string;
  allowReviewRouting?: boolean;
}): Promise<ResolvedCollection | null> {
  const { name, description, typeHint, allowReviewRouting } = params;

  const sessionId = getAgentSessionId();
  const result = await kernelCall<GatewayRouteReturnByName<"chain.resolveCollection">>("chain.resolveCollection", {
    entryName: name,
    entryDescription: description,
    ...(typeHint ? { typeHint } : {}),
    ...(allowReviewRouting ? { allowReviewRouting } : {}),
    ...(sessionId ? { agentSessionId: sessionId } : {}),
  });

  return result ?? null;
}
